import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Video, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';
import VerificacionCliente from '../components/VerificacionCliente';
import { logErrorToBackend } from '../utils/errorLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function AgendarCita() {
  const navigate = useNavigate();
  const [expedienteVerificado, setExpedienteVerificado] = useState(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [horaSeleccionada, setHoraSeleccionada] = useState(null);
  const [slotsDisponibles, setSlotsDisponibles] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [agendando, setAgendando] = useState(false);
  const [mesActual, setMesActual] = useState(new Date().getMonth());
  const [añoActual, setAñoActual] = useState(new Date().getFullYear());

  // Cuando el cliente es verificado
  const handleVerificado = (expediente) => {
    setExpedienteVerificado(expediente);
  };

  // Cargar slots disponibles cuando se selecciona una fecha
  const loadDisponibilidad = async (fecha) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/citas/disponibilidad?fecha=${fecha}`);
      if (response.ok) {
        const data = await response.json();
        if (data.disponible) {
          setSlotsDisponibles(data.slots);
        } else {
          setSlotsDisponibles([]);
          toast.info(data.mensaje || 'No hay horarios disponibles para esta fecha');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar disponibilidad');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleAgendarCita = async () => {
    if (!fechaSeleccionada || !horaSeleccionada) {
      toast.error('Por favor selecciona fecha y hora');
      return;
    }

    setAgendando(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/citas/agendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expediente_id: expedienteVerificado.id,
          fecha: fechaSeleccionada,
          hora: horaSeleccionada,
          tipo: 'virtual',
          ubicacion: null
        })
      });

      if (response.ok) {
        const [año, mes, dia] = fechaSeleccionada.split('-');
        const fechaLocal = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
        
        navigate('/cita-confirmada', {
          state: {
            cita: {
              fecha: fechaLocal.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              }),
              hora: horaSeleccionada,
              expediente: expedienteVerificado.numero_expediente
            }
          }
        });
      } else {
        const error = await response.json();
        const errorMsg = parseApiError(error.detail, 'Error al agendar cita');
        toast.error(errorMsg);
        logErrorToBackend({ tipo: 'agendar_cita_error', endpoint: '/api/citas/agendar', error: errorMsg });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexion al agendar cita. Verifique su internet e intente nuevamente.');
      logErrorToBackend({ tipo: 'agendar_cita_conexion_error', endpoint: '/api/citas/agendar', error: error.message });
    } finally {
      setAgendando(false);
    }
  };

  const getDiasDelMes = () => {
    const primerDia = new Date(añoActual, mesActual, 1);
    const ultimoDia = new Date(añoActual, mesActual + 1, 0);
    const dias = [];

    // Agregar días vacíos al inicio
    for (let i = 0; i < primerDia.getDay(); i++) {
      dias.push(null);
    }

    // Agregar todos los días del mes
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      dias.push(dia);
    }

    return dias;
  };

  const esDiaValido = (dia) => {
    if (!dia) return false;
    
    const fecha = new Date(añoActual, mesActual, dia);
    const fechaMinima = new Date(2026, 0, 19); // 19 de enero de 2026
    fechaMinima.setHours(0, 0, 0, 0);
    
    // Solo permitir fechas desde el 19 de enero de 2026 en adelante
    if (fecha < fechaMinima) return false;
    
    // Solo días laborales (Lun-Vie)
    const diaSemana = fecha.getDay();
    return diaSemana >= 1 && diaSemana <= 5;
  };

  const seleccionarDia = (dia) => {
    if (!esDiaValido(dia)) return;
    
    const fecha = new Date(añoActual, mesActual, dia);
    const fechaStr = fecha.toISOString().split('T')[0];
    setFechaSeleccionada(fechaStr);
    setHoraSeleccionada(null);
    loadDisponibilidad(fechaStr);
  };

  const mesAnterior = () => {
    if (mesActual === 0) {
      setMesActual(11);
      setAñoActual(añoActual - 1);
    } else {
      setMesActual(mesActual - 1);
    }
  };

  const mesSiguiente = () => {
    if (mesActual === 11) {
      setMesActual(0);
      setAñoActual(añoActual + 1);
    } else {
      setMesActual(mesActual + 1);
    }
  };

  // DOM estable: Ambos nodos siempre montados, se ocultan con display:none
  // Esto evita el crash insertBefore al resolver la promesa de buscar-expediente
  return (
    <div key="agendar-cita-stable-root" data-testid="agendar-cita-root">
      {/* Vista 1: Verificación - siempre montada, oculta cuando hay expediente */}
      <div style={{ display: expedienteVerificado ? 'none' : 'block' }}>
        <VerificacionCliente
          onVerificado={handleVerificado}
          titulo="Agenda tu Cita"
          descripcion="Verifica tu identidad para continuar"
        />
      </div>

      {/* Vista 2: Calendario - siempre montada, oculta cuando NO hay expediente */}
      <div style={{ display: expedienteVerificado ? 'block' : 'none' }}>
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Agenda tu Cita</h1>
              <p className="text-gray-400">Selecciona la fecha y hora que mejor te convenga</p>
            </div>

            {/* Info del Expediente */}
            {expedienteVerificado && (
              <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Expediente</p>
                      <p className="text-xl font-bold text-white">{expedienteVerificado.numero_expediente}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Solicitante</p>
                      <p className="text-lg text-white">{expedienteVerificado.solicitante?.nombre_completo}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendario */}
              <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                    Selecciona una Fecha
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Disponible: Lunes a Viernes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Navegación del mes */}
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={mesAnterior}
                      className="border-gray-700 text-white hover:bg-gray-700"
                    >
                      ←
                    </Button>
                    <span className="text-white font-semibold">
                      {MESES[mesActual]} {añoActual}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={mesSiguiente}
                      className="border-gray-700 text-white hover:bg-gray-700"
                    >
                      →
                    </Button>
                  </div>

                  {/* Días de la semana */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {DIAS.map((dia) => (
                      <div key={dia} className="text-center text-xs text-gray-500 font-semibold">
                        {dia}
                      </div>
                    ))}
                  </div>

                  {/* Días del mes */}
                  <div className="grid grid-cols-7 gap-2">
                    {getDiasDelMes().map((dia, index) => {
                      const esValido = esDiaValido(dia);
                      const fecha = dia ? new Date(añoActual, mesActual, dia).toISOString().split('T')[0] : null;
                      const esSeleccionado = fecha === fechaSeleccionada;
                      
                      let esNoDisponible = false;
                      if (dia) {
                        const fechaDia = new Date(añoActual, mesActual, dia);
                        const fechaMinima = new Date(2026, 0, 19);
                        const diaSemana = fechaDia.getDay();
                        const esDiaHabil = diaSemana >= 1 && diaSemana <= 5;
                        esNoDisponible = esDiaHabil && fechaDia < fechaMinima;
                      }

                      return (
                        <button
                          key={index}
                          onClick={() => dia && seleccionarDia(dia)}
                          disabled={!esValido}
                          className={`
                            aspect-square rounded-lg text-sm font-medium transition-all relative flex flex-col items-center justify-center p-1
                            ${!dia ? 'invisible' : ''}
                            ${esSeleccionado ? 'bg-yellow-500 text-black' : ''}
                            ${esValido && !esSeleccionado ? 'bg-gray-700 text-white hover:bg-gray-600' : ''}
                            ${!esValido && dia ? 'bg-gray-900 text-gray-600 cursor-not-allowed' : ''}
                          `}
                        >
                          <span>{dia}</span>
                          {esNoDisponible && (
                            <span className="text-[8px] text-red-400 leading-tight mt-0.5 text-center">
                              No disp.
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Horarios y Detalles */}
              <div className="space-y-6">
                {/* Horarios */}
                {fechaSeleccionada && (
                  <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Clock className="w-5 h-5 text-yellow-500" />
                        Selecciona un Horario
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        Slots disponibles para {fechaSeleccionada}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingSlots ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                        </div>
                      ) : slotsDisponibles.length > 0 ? (
                        <div className="grid grid-cols-3 gap-2">
                          {slotsDisponibles.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setHoraSeleccionada(slot)}
                              className={`
                                p-3 rounded-lg text-sm font-medium transition-all
                                ${horaSeleccionada === slot ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-white hover:bg-gray-600'}
                              `}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-8">No hay horarios disponibles</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Tipo de Cita - Solo Virtual */}
                {horaSeleccionada && (
                  <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Video className="w-5 h-5 text-yellow-500" />
                        Cita Virtual
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                        <p className="text-gray-300 text-sm">
                          ✓ Tu cita será <strong>100% virtual</strong> por Google Meet
                        </p>
                        <p className="text-gray-400 text-xs mt-2">
                          Recibirás el link de la reunión en tu correo electrónico
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Botón Confirmar */}
                {fechaSeleccionada && horaSeleccionada && (
                  <Button
                    onClick={handleAgendarCita}
                    disabled={agendando}
                    data-testid="confirmar-cita-btn"
                    className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg"
                  >
                    {agendando ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Agendando...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Confirmar Cita
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Info adicional */}
            <Card className="bg-blue-900/20 border-blue-500/30 mt-6">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-3">Información Importante</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• <strong>Disponibilidad:</strong> A partir del 19 de Enero de 2026</li>
                  <li>• <strong>Horarios:</strong> Lunes a Viernes, 9:00 AM - 12:00 PM y 2:00 PM - 5:00 PM</li>
                  <li>• <strong>Duración:</strong> 30 minutos por cita</li>
                  <li>• <strong>Modalidad:</strong> Solo citas virtuales por Google Meet</li>
                  <li>• Recibirás un email de confirmación con el link de la reunión</li>
                  <li>• Si necesitas reprogramar, podrás hacerlo desde el email de confirmación</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
